import type { ReactNode } from "react";
import { useEffect } from "react";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getContrastForeground } from "@/theme/contrast";
import { institutionThemeToCssVars } from "@/theme/themeFromInstitution";
import { SHAPE_UI } from "@/theme/theme";

/**
 * Maps institution theme props to CSS custom properties applied to :root.
 * These vars are the single source of truth; do not define them in index.css.
 */
const themePropsToCssVars = (props: {
  backgroundColor: string;
  primaryColor: string;
  textColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  quaternaryColor: string;
  neutralColor: string;
  borderColor: string;
}): Record<string, string> => {
  const contrastOpts = { light: props.neutralColor, dark: props.textColor };
  return {
    "--background": props.backgroundColor,
    "--foreground": props.textColor,
    "--primary": props.primaryColor,
    "--primary-foreground": getContrastForeground(
      props.primaryColor,
      contrastOpts,
    ),
    "--secondary": props.secondaryColor,
    "--secondary-foreground": getContrastForeground(
      props.secondaryColor,
      contrastOpts,
    ),
    "--muted": props.tertiaryColor,
    "--muted-foreground": getContrastForeground(
      props.tertiaryColor,
      contrastOpts,
    ),
    "--accent": props.secondaryColor,
    "--accent-foreground": getContrastForeground(
      props.secondaryColor,
      contrastOpts,
    ),
    "--border": props.borderColor,
    "--input": props.borderColor,
    "--ring": props.primaryColor,
    "--card": props.neutralColor,
    "--card-foreground": props.textColor,
    "--card-title": props.primaryColor,
    "--popover": props.neutralColor,
    "--popover-foreground": props.textColor,
    "--sidebar": props.backgroundColor,
    "--sidebar-foreground": getContrastForeground(
      props.backgroundColor,
      contrastOpts,
    ),
    "--sidebar-primary": props.primaryColor,
    "--sidebar-primary-foreground": getContrastForeground(
      props.primaryColor,
      contrastOpts,
    ),
    "--sidebar-accent": props.secondaryColor,
    "--sidebar-accent-foreground": getContrastForeground(
      props.secondaryColor,
      contrastOpts,
    ),
    "--sidebar-border": props.borderColor,
    "--sidebar-ring": props.primaryColor,
  };
};

/**
 * Applies institution theme to :root so header, sidebar, and portaled UI get it.
 * Main content (catalog, etc.) uses data-theme="content" in PagesShell so it stays neutral.
 *
 * Single source of truth: the CSS variables in THEME_VARS_PROVIDER_OWNED_KEYS
 * are set only here. Do not define those vars in index.css or other stylesheets.
 */
export const ThemeVarsProvider = ({ children }: { children: ReactNode }) => {
  const { props } = useThemeColor();

  useEffect(() => {
    document.documentElement.setAttribute("data-shape", SHAPE_UI);
    return () => document.documentElement.removeAttribute("data-shape");
  }, []);

  useEffect(() => {
    const vars = {
      ...themePropsToCssVars(props),
      ...institutionThemeToCssVars(props),
    };
    const root = document.documentElement;
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
    return () => {
      for (const key of Object.keys(vars)) {
        root.style.removeProperty(key);
      }
    };
  }, [props]);

  return <div>{children}</div>;
};
